/**
 * ProblemAlert — renders an ApiError (RFC-7807) by its STABLE machine `code`,
 * with Spanish (es-MX) messages. Switches on `code`, never the human `detail`.
 *
 * Auth/forbidden (401/403) are normally handled by the route guards (redirect
 * to sign-in / forbidden page), so screens pass non-auth errors here. If an
 * auth error does reach this component it still renders a sane message rather
 * than falling back to mock.
 */

import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { ApiError, isApiError } from "../api/client";

type Mapped = { title: string; message: string };

function messageFor(error: ApiError | Error): Mapped {
  if (!isApiError(error)) {
    return {
      title: "Error de red",
      message:
        "No se pudo contactar al servicio. Verifica tu conexión e inténtalo de nuevo.",
    };
  }
  switch (error.code) {
    case "AUTH_MISSING_TOKEN":
      return {
        title: "Sesión requerida",
        message: "Tu sesión expiró o no has iniciado sesión. Vuelve a iniciar sesión.",
      };
    case "AUTH_FORBIDDEN_ROLE":
      return {
        title: "Acceso denegado",
        message: "Tu cuenta no tiene permisos para esta acción.",
      };
    case "ROUND_NOT_FOUND":
      return {
        title: "Ronda no encontrada",
        message:
          "No existe esa ronda, o el reporte aún no está disponible (la ronda no ha terminado).",
      };
    case "EXPEDIENTE_NOT_FOUND":
      return { title: "Expediente no encontrado", message: "No existe ese expediente." };
    case "AUDIT_NOT_FOUND":
      return {
        title: "Registro de auditoría no encontrado",
        message: "No hay una decisión automatizada registrada para este expediente.",
      };
    case "ADJUDICATION_VALIDATION":
      return {
        title: "Dictamen inválido",
        message: "Revisa los campos del dictamen e inténtalo de nuevo.",
      };
    case "STORAGE_UNAVAILABLE":
      return {
        title: "Almacenamiento no disponible",
        message: "El servicio de almacenamiento no está disponible. Inténtalo más tarde.",
      };
    case "REPORT_UNAVAILABLE":
      return {
        title: "Reporte no disponible",
        message:
          "Aún no hay un reporte para esta ronda, o el reporte está dañado. Inténtalo cuando la ronda termine.",
      };
    case "PROFILE_ID_VIOLATION":
      return {
        title: "Identificador no permitido por el perfil",
        message:
          error.detail ??
          "El identificador del expediente no cumple la regla del perfil de validación (EXACER: debe ser una CURP).",
      };
    case "PROFILE_DOCUMENT_VIOLATION":
      return {
        title: "Documentos no válidos para el perfil",
        message:
          error.detail ??
          "El conjunto de documentos no coincide con los tipos que exige el perfil de validación.",
      };
    case "INTEGRITY_VERIFICATION_FAILED":
      return {
        title: "Fallo de integridad",
        message:
          "Se detectó un registro de auditoría alterado. Por seguridad, no se mostrará la información sin verificar.",
      };
    default:
      return {
        title: error.title ?? "Ocurrió un error",
        message: error.detail ?? "Ocurrió un error inesperado. Inténtalo de nuevo.",
      };
  }
}

export function ProblemAlert({
  error,
  onRetry,
}: {
  error: ApiError | Error | null | undefined;
  onRetry?: () => void;
}) {
  if (!error) return null;
  const { title, message } = messageFor(error);
  const fieldErrors = isApiError(error) ? error.errors : undefined;

  return (
    <Alert
      severity="error"
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Reintentar
          </Button>
        ) : undefined
      }
      sx={{ my: 2 }}
    >
      <AlertTitle>{title}</AlertTitle>
      <Typography variant="body2">{message}</Typography>
      {fieldErrors && fieldErrors.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
          {fieldErrors.map((fe, i) => (
            <li key={i}>
              <Typography variant="caption">
                {fe.loc.join(".")}: {fe.msg}
              </Typography>
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
}
